import type { AxiosRequestConfig } from "axios";
import { useMemo } from "react";
import type { CognitoUser } from "@aws-amplify/auth";
import { apiPaths } from "../../constants/apiPaths";
import { useAuthenticationContext } from "../../hooks/context/useAuthenticationContext";
import { getApiClients } from "../utils/apiClients";
import { authenticationService } from "../../services/authentication";
import type { ClassroomMember, SubmissionItemsListResponse, UsersListResponse } from "../backend_client";
import type {
  AddClassroomMemberParameters,
  BackendService,
  Classroom,
  CreateClassroomParameters,
  StartMockExamParameters,
  CreatePracticeTestParameters,
  CreateSubmissionParameters,
  CreateUserParameters,
  DeleteClassroomMemberParameters,
  DeleteClassroomParameters,
  DeleteUserParameters,
  Exercise,
  ExerciseAttributes,
  ExercisesPerLevel,
  GetClassroomParameters,
  GetExerciseByIdParameters,
  GetExerciseByLocationParameters,
  GetExercisesByLevelCode,
  GetPracticeTestParameters,
  GetSchoolInfoParameters,
  GetSchoolUsersParameters,
  GetUserParameters,
  ImportResults,
  ImportUsersParameters,
  ListClassroomMembersParameters,
  ListMockExamSubmissionsParameters,
  ListPracticeTestsParameters,
  ListUserClassroomsParameters,
  PracticeTest,
  PracticeTestAnswers,
  School,
  SchoolSignUpParameters,
  Subdomain,
  SubdomainParameters,
  Submission,
  UpdateClassroomParameters,
  UpdateMockExamSubmissionParameters,
  UpdatePracticeTestParameters,
  UpdateSchoolParameters,
  UpdateUserBodyParameters,
  UpdateUserParameters,
  User,
  UserCompletedExercisesPerLevel,
  MockExamSubmission,
  GetUserCompletedExercises,
  GetSubmissionParameters,
  ListSubmissionsParameters,
  AuthenticationService,
} from "./types";

export const useBackendServices = () => {
  const { getJwtToken, resetJwtToken, user } = useAuthenticationContext();

  const authServiceWrapper: AuthenticationService = useMemo(
    () => ({
      getCurrentSession: () => authenticationService.getCurrentSession(),
      getCognitoUser: () => {
        if (!user) {
          throw new Error("No authenticated user available");
        }
        return user as unknown as CognitoUser;
      },
      getJwtToken: async () => {
        const session = await authenticationService.getCurrentSession();
        return session.getIdToken().getJwtToken();
      },
      resetJwtToken,
    }),
    [user, resetJwtToken],
  );

  const { backendAPIClient, loginAPIClient } = getApiClients(authServiceWrapper);

  const getBackendAPIConfig = async () => {
    const jwtToken = await getJwtToken();
    const config: AxiosRequestConfig = {
      headers: {
        "Content-type": "application/json",
        Authorization: jwtToken,
      },
    };
    return config;
  };

  const addClassroomMember = async ({ ClassroomId, UserId, Role }: AddClassroomMemberParameters) => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.post<void>(`${apiPaths.classrooms}/${ClassroomId}/members`, { UserId, Role }, config);
    return response.data;
  };

  const createClassroom = async ({ Name, AccessToCorrectAnswers, ExerciseRepetition, AccessToSpeakingTranscripts }: CreateClassroomParameters) => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.post<Classroom>(
      apiPaths.classrooms,
      { Name, AccessToCorrectAnswers, ExerciseRepetition, AccessToSpeakingTranscripts },
      config,
    );
    return response.data;
  };

  const startMockExam = async ({ Level, Number }: StartMockExamParameters) => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.post<MockExamSubmission>(apiPaths.mockExams, { Level, Number }, config);
    return response.data;
  };

  const createPracticeTest = async ({ Level, Section }: CreatePracticeTestParameters) => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.post<PracticeTest>(apiPaths.practiceTests, { Level, Section }, config);
    return response.data;
  };

  const createSubmission = async ({ ExerciseId, Answers: AnswersMap, Candidates }: CreateSubmissionParameters) => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.post<Submission>(apiPaths.submissions, { ExerciseId, Answers: Object.fromEntries(AnswersMap), Candidates }, config);
    return response.data;
  };

  const createUser = async ({ Name, Email, Role, AccessToLevels, Classrooms }: CreateUserParameters) => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.post<User>(apiPaths.users, { Name, Email, Role, AccessToLevels, Classrooms }, config);
    return response.data;
  };

  const deleteClassroom = async ({ ClassroomId }: DeleteClassroomParameters) => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.delete<void>(`${apiPaths.classrooms}/${ClassroomId}`, config);
    return response.data;
  };

  const deleteClassroomMember = async ({ ClassroomId, UserId, Role }: DeleteClassroomMemberParameters) => {
    const config = await getBackendAPIConfig();
    config.params = {
      role: Role,
    };
    const response = await backendAPIClient.delete<void>(`${apiPaths.classrooms}/${ClassroomId}/members/${UserId}`, config);
    return response.data;
  };

  const deleteUser = async ({ UserId, Role }: DeleteUserParameters) => {
    const config = await getBackendAPIConfig();
    config.params = {
      role: Role,
    };
    await backendAPIClient.delete<void>(`${apiPaths.users}/${UserId}`, config);
    return;
  };

  const getClassroom = async ({ ClassroomId }: GetClassroomParameters) => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.get<Classroom>(`${apiPaths.classrooms}/${ClassroomId}`, config);
    return response.data;
  };

  const getExerciseById = async ({ exerciseId }: GetExerciseByIdParameters) => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.get<ExerciseAttributes>(`${apiPaths.exercises}/${exerciseId}`, config);
    return response.data;
  };

  const getExerciseByPosition = async ({ level, section, part, number, role }: GetExerciseByLocationParameters) => {
    const config = await getBackendAPIConfig();
    const exercisePath = `${level}/${section}/${part}/${number.toString()}`;
    const response = await backendAPIClient.get<Exercise>(`${apiPaths.exercises}/${exercisePath}?role=${role}`, config);
    return response.data;
  };

  const getExerciseCounters = async ({ level }: GetExercisesByLevelCode) => {
    const config = await getBackendAPIConfig();
    const path = apiPaths.levels.replace(":level", level);
    const response = await backendAPIClient.get<ExercisesPerLevel>(path, config);
    return response.data;
  };

  const getPracticeTest = async ({ practiceTestId, userId, role }: GetPracticeTestParameters) => {
    const config = await getBackendAPIConfig();
    let path = `${apiPaths.practiceTests}/${practiceTestId}`;
    if (userId !== undefined && role !== undefined) {
      path += `?user=${userId}&role=${role}`;
    }
    const response = await backendAPIClient.get<PracticeTest>(path, config);
    return response.data;
  };

  const getSchoolInfo = async ({ schoolId }: GetSchoolInfoParameters) => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.get<School>(`${apiPaths.schools}/${schoolId}`, config);
    return response.data;
  };

  const getSchoolUsers = async ({ role }: GetSchoolUsersParameters) => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.get<UsersListResponse>(`${apiPaths.users}?role=${role}`, config);
    return response.data;
  };

  const getSubdomain = async ({ Name }: SubdomainParameters) => {
    const response = await loginAPIClient.get<Subdomain>(`${apiPaths.subdomains}/${Name}`);
    return response.data;
  };

  const getSubmission = async ({ SubmissionId, UserId, Role }: GetSubmissionParameters) => {
    const config = await getBackendAPIConfig();
    let path = `${apiPaths.submissions}/${SubmissionId}`;
    if (UserId !== undefined && Role !== undefined) {
      path += `?user=${UserId}&role=${Role}`;
    }
    const response = await backendAPIClient.get<Submission>(path, config);
    return response.data;
  };

  const getUser = async ({ UserId, Role }: GetUserParameters) => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.get<User>(`${apiPaths.users}/${UserId}?role=${Role}`, config);
    return response.data;
  };

  const getUserCompletedExercises = async ({ UserId, Level }: GetUserCompletedExercises) => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.get<UserCompletedExercisesPerLevel>(
      apiPaths.userCompletedExercises.replace(":userId", UserId).replace(":level", Level),
      config,
    );
    return response.data;
  };

  const importUsers = async ({ UserList }: ImportUsersParameters) => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.post<ImportResults>(apiPaths.import, { UserList }, config);
    return response.data;
  };

  const listClassroomMembers = async ({ ClassroomId }: ListClassroomMembersParameters) => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.get<ClassroomMember[]>(`${apiPaths.classrooms}/${ClassroomId}/members`, config);
    return response.data;
  };

  const listClassrooms = async () => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.get<Classroom[]>(apiPaths.classrooms, config);
    return response.data;
  };

  const listMockExamSubmissions = async ({ level, userId, role }: ListMockExamSubmissionsParameters) => {
    const config = await getBackendAPIConfig();
    let path = `${apiPaths.mockExams}?level=${level}`;
    if (userId !== undefined && role !== undefined) {
      path += `&user=${userId}&role=${role}`;
    }
    const response = await backendAPIClient.get<MockExamSubmission[]>(path, config);
    return response.data;
  };

  const listPracticeTests = async ({ level, section, userId, role }: ListPracticeTestsParameters) => {
    const config = await getBackendAPIConfig();
    let path = `${apiPaths.practiceTests}?level=${level}`;
    if (section !== undefined) {
      path += `&section=${section}`;
    }
    if (userId !== undefined && role !== undefined) {
      path += `&user=${userId}&role=${role}`;
    }
    const response = await backendAPIClient.get<PracticeTest[]>(path, config);
    return response.data;
  };

  const listSubmissions = async ({ level, section, part, userId, role }: ListSubmissionsParameters) => {
    const config = await getBackendAPIConfig();
    let path = `${apiPaths.submissions}?level=${level}&section=${section}`;
    if (part !== undefined) {
      path += `&part=${part}`;
    }
    if (userId !== undefined && role !== undefined) {
      path += `&user=${userId}&role=${role}`;
    }
    const response = await backendAPIClient.get<SubmissionItemsListResponse>(path, config);
    return response.data;
  };

  const listUserClassrooms = async ({ UserId, Role }: ListUserClassroomsParameters) => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.get<Classroom[]>(`${apiPaths.userClassrooms.replace(":userId", UserId)}?role=${Role}`, config);
    return response.data;
  };

  const schoolSignUp = async ({ Name, Email, Password }: SchoolSignUpParameters) => {
    const response = await loginAPIClient.post<School>(apiPaths.signUp, { Name, Email, Password });
    return response.data;
  };

  const updateClassroom = async ({
    ClassroomId,
    Name,
    AccessToCorrectAnswers,
    ExerciseRepetition,
    AccessToTranscripts,
    BlockedExercises,
    AccessToMockExams,
    AccessToMockExamResults,
  }: UpdateClassroomParameters) => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.patch<Classroom>(
      `${apiPaths.classrooms}/${ClassroomId}`,
      { Name, AccessToCorrectAnswers, ExerciseRepetition, AccessToTranscripts, BlockedExercises, AccessToMockExams, AccessToMockExamResults },
      config,
    );
    return response.data;
  };

  type ResultType = { [key: string]: { [key: string]: string | Blob } };

  const transformType = (obj: PracticeTestAnswers): ResultType => {
    const result: ResultType = {};
    for (const [key, value] of Object.entries(obj)) {
      const answers = Object.fromEntries(value);
      for (const [k, v] of Object.entries(answers)) {
        answers[k] = v;
      }
      result[key] = answers;
    }
    return result;
  };

  const updateMockExamSubmission = async ({
    mockExamSubmissionId,
    Section,
    SelectedWriting,
    TimeSpent,
    Status,
    Answers,
    Candidates,
    CompletedListeningParts,
  }: UpdateMockExamSubmissionParameters) => {
    const config = await getBackendAPIConfig();
    const serializableAnswers = transformType(Answers);
    const response = await backendAPIClient.patch<MockExamSubmission>(
      `${apiPaths.mockExams}/${mockExamSubmissionId}`,
      { Section, TimeSpent, Status, Answers: serializableAnswers, SelectedWriting, Candidates, CompletedListeningParts },
      config,
    );
    return response.data;
  };

  const updatePracticeTest = async ({ practiceTestId, TimeSpent, Status, Answers, Candidates }: UpdatePracticeTestParameters) => {
    const config = await getBackendAPIConfig();
    const serializableAnswers = transformType(Answers);
    const response = await backendAPIClient.patch<PracticeTest>(
      `${apiPaths.practiceTests}/${practiceTestId}`,
      { TimeSpent, Status, Answers: serializableAnswers, Candidates },
      config,
    );
    return response.data;
  };

  const updateSchool = async ({
    schoolId,
    Name,
    SchoolSubdomain,
    SchoolLogo,
    SchoolBackground,
    BillingName,
    Country,
    City,
    Address,
    ZipCode,
    TaxId,
  }: UpdateSchoolParameters) => {
    const config = await getBackendAPIConfig();
    const response = await backendAPIClient.patch<School>(
      `${apiPaths.schools}/${schoolId}`,
      { Name, SchoolSubdomain, SchoolLogo, SchoolBackground, BillingName, Country, City, Address, ZipCode, TaxId },
      config,
    );
    return response.data;
  };

  const updateUser = async ({
    UserId,
    Role,
    Name,
    Email,
    Active,
    AccessToLevels,
    Password,
    PermanentPassword,
    AccessToCorrectAnswers,
    AccessToTranscripts,
    ExerciseRepetition,
    BlockedExercises,
    ProfilePicture,
  }: UpdateUserParameters) => {
    const config = await getBackendAPIConfig();
    config.params = {
      role: Role,
    };
    const body: UpdateUserBodyParameters = {};
    if (Name) body.Name = Name;
    if (Email) body.Email = Email;
    if (Active !== undefined) body.Active = Active;
    if (AccessToLevels) body.AccessToLevels = AccessToLevels;
    if (AccessToCorrectAnswers !== undefined) body.AccessToCorrectAnswers = AccessToCorrectAnswers;
    if (AccessToTranscripts !== undefined) body.AccessToTranscripts = AccessToTranscripts;
    if (ExerciseRepetition !== undefined) body.ExerciseRepetition = ExerciseRepetition;
    if (BlockedExercises) body.BlockedExercises = BlockedExercises;
    if (ProfilePicture) body.ProfilePicture = ProfilePicture;
    if (Password && PermanentPassword !== undefined) {
      body.Password = Password;
      body.PermanentPassword = PermanentPassword;
    }
    const response = await backendAPIClient.patch<User>(`${apiPaths.users}/${UserId}`, body, config);
    return response.data;
  };

  const backendService: BackendService = {
    addClassroomMember: addClassroomMember,
    createClassroom: createClassroom,
    createPracticeTest: createPracticeTest,
    createSubmission: createSubmission,
    createUser: createUser,
    deleteClassroom: deleteClassroom,
    deleteClassroomMember: deleteClassroomMember,
    deleteUser: deleteUser,
    getClassroom: getClassroom,
    getExerciseById: getExerciseById,
    getExerciseByPosition: getExerciseByPosition,
    getExerciseCounters: getExerciseCounters,
    getPracticeTest: getPracticeTest,
    getSchoolInfo: getSchoolInfo,
    getSchoolUsers: getSchoolUsers,
    getSubdomain: getSubdomain,
    getSubmission: getSubmission,
    getUser: getUser,
    getUserCompletedExercises: getUserCompletedExercises,
    importUsers: importUsers,
    listClassroomMembers: listClassroomMembers,
    listClassrooms: listClassrooms,
    listMockExamSubmissions: listMockExamSubmissions,
    listPracticeTests: listPracticeTests,
    listSubmissions: listSubmissions,
    listUserClassrooms: listUserClassrooms,
    schoolSignUp: schoolSignUp,
    startMockExam: startMockExam,
    updateClassroom: updateClassroom,
    updateMockExamSubmission: updateMockExamSubmission,
    updatePracticeTest: updatePracticeTest,
    updateSchool: updateSchool,
    updateUser: updateUser,
  };

  return {
    backendService,
  };
};
